import React from 'react';

/**
 * The capability form's shared vocabulary. Everything here is an existing app
 * token — the zinc surfaces, the subtle borders, the muted mono captions the
 * reviewer detail column already uses — so the form reads as part of the panel
 * it is embedded in rather than as a third visual language.
 */
export const fieldLabel =
  'block text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-1';

export const fieldCaption = 'font-mono text-[10px] leading-relaxed text-[var(--app-text-muted)]';

export const groupHeading =
  'text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--app-text-muted)]';

/**
 * Whether the layers ABOVE this form are actually known yet. An "inherits: off"
 * caption drawn from the hard defaults while the account read is still in
 * flight — or after it failed — is not a caption, it is a wrong answer, so the
 * two unresolved states say so instead of naming a value.
 */
export type InheritedStatus = 'loading' | 'error' | 'ready';

export const INHERITED_STATUS_TEXT: Record<Exclude<InheritedStatus, 'ready'>, string> = {
  loading: 'reading your reviewer settings…',
  error: "couldn't read your reviewer settings",
};

/**
 * The caption under an unset control, and the button that unsets a set one.
 * They are one component because they are one statement: "this field is (not)
 * yours, and here is what happens if it isn't".
 */
export const InheritCaption: React.FC<{
  field: string;
  inherits: string;
  from: string;
  isSet: boolean;
  onReset: () => void;
  /** Defaults to `ready` — a layer with nothing above it has nothing to read. */
  status?: InheritedStatus;
}> = ({ field, inherits, from, isSet, onReset, status = 'ready' }) => {
  const unresolved = status === 'ready' ? '' : INHERITED_STATUS_TEXT[status];
  let text: string;
  if (unresolved) {
    // A field set HERE is still a fact we know; only the inherited half is not.
    text = isSet ? `set here · ${unresolved}` : unresolved;
  } else {
    text = isSet ? `set here · ${from}: ${inherits}` : `inherits: ${inherits}`;
  }

  return (
    <div className="mt-1 flex items-center gap-2">
      <span
        className={fieldCaption}
        data-testid={`review-inherits-${field}`}
        data-status={status}
        title={`Unset, this field takes ${from}'s value`}
      >
        {text}
      </span>
      {isSet && (
        <button
          type="button"
          className="btn btn-sm btn-ghost !px-1.5 !py-0 font-mono text-[10px]"
          data-testid={`review-reset-${field}`}
          title={`Unset this field so it inherits from ${from} again`}
          onClick={onReset}
        >
          Reset
        </button>
      )}
    </div>
  );
};

/** A boolean capability, in the app's existing "theme-switch" language. */
export const CapabilitySwitch: React.FC<{
  field: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ field, label, checked, onToggle }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    data-testid={`review-toggle-${field}`}
    className="theme-switch w-full justify-between"
    onClick={onToggle}
  >
    <span className="theme-switch-label">{label}</span>
    <span className="theme-switch-track">
      <span className={`theme-switch-thumb ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </span>
  </button>
);
